import type { CorrectionLetterResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchCorrectionLetterApi } from "../../../../../api/client";
import { correctionLetterKeys } from "./correctionLetterKeys";

/**
 * Shared queryOptions for the Correction Letter modal (key + queryFn). Backed by
 * the generated WorkbenchCorrectionLetterApi.getCorrectionLetter. Gated on
 * `enabled` in addition to `approvalId` so callers only fetch while the modal is
 * open (mirrors `useDealerInfo`). `staleTime: 0` so reopening the modal always
 * reflects the latest saved selections.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run (e.g. the modal is open).
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function correctionLetterQuery(approvalId: string, enabled: boolean) {
  return queryOptions({
    queryKey: correctionLetterKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<CorrectionLetterResponse> => {
      const response = await workbenchCorrectionLetterApi.getCorrectionLetter(approvalId, { signal });
      return response.data;
    },
    enabled: enabled && !!approvalId,
    staleTime: 0,
  });
}

/**
 * Reads the Correction Letter reasons for a deal. Disabled until both
 * `approvalId` is present and `enabled` is true.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run (e.g. the modal is open).
 * @returns {ReturnType<typeof useQuery<CorrectionLetterResponse>>} The TanStack Query result.
 */
export function useCorrectionLetter(approvalId: string, enabled: boolean) {
  return useQuery(correctionLetterQuery(approvalId, enabled));
}
